import { useState } from 'react';
import { FileSpreadsheet, FileText, Plus, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { createApplicants, deleteApplication, getApplicantsExportUrl, previewApplicantImport, triggerDownload } from '@/lib/api';

const emptyForm = { first_name: '', last_name: '', email: '', program: '', intended_semester: '' };
const outlineClass = 'border-[#062B1A]/15 text-[#062B1A]';

function errorMessage(error, fallback) {
  const detail = error?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
}

export default function ApplicantActions({ selectedApplications, programs, onChanged }) {
  const [dialog, setDialog] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const noSelection = selectedApplications.length === 0;

  const open = (name) => {
    setForm(emptyForm);
    setRows([]);
    setError('');
    setDialog(name);
  };

  const save = async (applicants) => {
    setBusy(true);
    setError('');
    try {
      const { data } = await createApplicants(applicants);
      toast.success(`${data.created} applicant${data.created === 1 ? '' : 's'} added`);
      setDialog('');
      onChanged();
    } catch (err) {
      setError(errorMessage(err, 'Could not add applicants. Check the names, email, program, and starting date.'));
    } finally {
      setBusy(false);
    }
  };

  const preview = async (file) => {
    setRows([]);
    setError('');
    if (!file) return;
    setBusy(true);
    try {
      const { data } = await previewApplicantImport(file);
      setRows(data.rows);
    } catch (err) {
      setError(errorMessage(err, 'Could not read applicant CSV.'));
    } finally {
      setBusy(false);
    }
  };

  const exportSelected = async (format) => {
    setBusy(true);
    try {
      await triggerDownload(getApplicantsExportUrl(format, selectedApplications.map(({ id }) => id)), `applicants_selected.${format}`);
    } catch (err) {
      toast.error(`Could not download ${format.toUpperCase()} export`);
    } finally {
      setBusy(false);
    }
  };

  const deleteSelected = async () => {
    setBusy(true);
    const failures = [];
    let deleted = 0;
    for (const applicant of selectedApplications) {
      try {
        await deleteApplication(applicant.id);
        deleted += 1;
      } catch (err) {
        failures.push(`${applicant.first_name} ${applicant.last_name}: ${errorMessage(err, 'Could not delete applicant')}`);
      }
    }
    if (deleted) toast.success(`${deleted} applicant${deleted === 1 ? '' : 's'} deleted`);
    if (failures.length) {
      setError(failures.join('\n'));
    } else {
      setDialog('');
    }
    onChanged();
    setBusy(false);
  };

  return (
    <>
      <Button type="button" size="sm" disabled={busy} onClick={() => open('add')} className="bg-[#062B1A] text-white hover:bg-[#041B10]"><Plus className="w-4 h-4 mr-2" />Add Applicant</Button>
      <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => open('import')} className={outlineClass}><Upload className="w-4 h-4 mr-2" />Import Applicants</Button>
      <Button type="button" size="sm" variant="outline" disabled={busy || noSelection} onClick={() => exportSelected('csv')} className={outlineClass}><FileSpreadsheet className="w-4 h-4 mr-2" />Export CSV</Button>
      <Button type="button" size="sm" variant="outline" disabled={busy || noSelection} onClick={() => exportSelected('pdf')} className={outlineClass}><FileText className="w-4 h-4 mr-2" />Export PDF</Button>
      <Button type="button" size="sm" disabled={busy || noSelection} onClick={() => open('delete')} className="bg-red-700 text-white hover:bg-red-800"><Trash2 className="w-4 h-4 mr-2" />Bulk Delete</Button>

      <Dialog open={Boolean(dialog)} onOpenChange={(isOpen) => { if (!isOpen && !busy) setDialog(''); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{dialog === 'add' ? 'Add Applicant' : dialog === 'import' ? 'Import Applicants' : 'Delete applicants?'}</DialogTitle>
            <DialogDescription>
              {dialog === 'add' ? 'Create an applicant record. You can complete the remaining details in their profile.'
                : dialog === 'import' ? 'Upload a CSV, review the applicants, then import.'
                  : `Permanently delete ${selectedApplications.length} selected applicant(s) and their application documents. Applicants linked to students cannot be deleted.`}
            </DialogDescription>
          </DialogHeader>
          {error && <p role="alert" className="whitespace-pre-line text-sm text-red-700">{error}</p>}
          {dialog === 'add' && (
            <form onSubmit={(event) => { event.preventDefault(); save([form]); }} className="space-y-4">
              {[
                ['first_name', 'First name', 'text'], ['last_name', 'Last name', 'text'],
                ['email', 'Email', 'email'], ['program', 'Program', 'text'],
                ['intended_semester', 'Starting date (YYYY-MM-01 or asap)', 'text'],
              ].map(([key, label, type]) => (
                <div key={key} className="space-y-1.5">
                  <Label htmlFor={`applicant-${key}`}>{label}</Label>
                  <Input id={`applicant-${key}`} type={type} required={['first_name', 'last_name', 'email'].includes(key)}
                    disabled={busy} list={key === 'program' ? 'applicant-programs' : undefined}
                    value={form[key]} onChange={(event) => setForm((current) => ({ ...current, [key]: event.target.value }))} />
                </div>
              ))}
              <datalist id="applicant-programs">{programs.map((program) => <option key={program} value={program} />)}</datalist>
              <DialogFooter><Button type="submit" disabled={busy} className="bg-[#062B1A] text-white">{busy ? 'Saving…' : 'Add Applicant'}</Button></DialogFooter>
            </form>
          )}
          {dialog === 'import' && (
            <div className="space-y-4">
              <a className="text-sm text-[#062B1A] underline" download="applicants_template.csv"
                href={`data:text/csv;charset=utf-8,${encodeURIComponent('first_name,last_name,email,program,intended_semester\n')}`}>Download CSV template</a>
              <Label htmlFor="applicant-import-file" className="block">Applicant CSV</Label>
              <Input id="applicant-import-file" type="file" accept=".csv,text/csv" disabled={busy} onChange={(event) => preview(event.target.files?.[0])} />
              {rows.length > 0 && <div className="max-h-60 overflow-auto rounded border p-3 text-sm">
                <p className="mb-2 font-semibold">{rows.length} applicant(s) ready to import</p>
                {rows.map((row) => <p key={row.email}>{row.first_name} {row.last_name} — {row.email}</p>)}
              </div>}
              <DialogFooter><Button disabled={busy || !rows.length} onClick={() => save(rows)} className="bg-[#062B1A] text-white">{busy ? 'Processing…' : 'Import Applicants'}</Button></DialogFooter>
            </div>
          )}
          {dialog === 'delete' && <DialogFooter>
            <Button variant="outline" disabled={busy} onClick={() => setDialog('')}>Cancel</Button>
            <Button disabled={busy || noSelection} onClick={deleteSelected} className="bg-red-700 text-white hover:bg-red-800">{busy ? 'Deleting…' : 'Delete applicants'}</Button>
          </DialogFooter>}
        </DialogContent>
      </Dialog>
    </>
  );
}
